/* eslint-disable max-lines */

import {ArrUtil, HTMLUtil, logger, Util, UUID} from 'yougile-util'
import {
  ChatMessage,
  Checklist,
  DataObjectId,
  getChecklists,
  getChunksWithFixedText,
  getMatchedMentions,
  GroupChat,
  Hub,
  newsCompanyId,
  youGileUserId
} from 'yougile-model'
import {Sync} from '@client/sync'
import {idTasksApi} from '@extensions/id-tasks/id-tasks-store'
import {NotificationSettingsService} from '@extensions/notifications-settings-service'
import {ChatProxy} from '@extensions/proxies/chat-proxy'
import {ChatUtils} from '../../extensions/chat-utils'
import {globals} from '../../extensions/globals-base'
import {Task} from '../../extensions/task'
import {apiChatList, FilterChatListType} from '../components/api-chat-list'
import {ContextObject} from '../context-object'
import {Item} from './item'
import {Items} from './items'
import {MessageObj} from './message-obj'
import {UserObj} from './user-obj'
import {Users} from './users'

export interface ChatInputKeydownData {
  altKey: boolean
  ctrlKey: boolean
  keyCode: number
  keystroke: number
  metaKey: boolean
  shiftKey: boolean
}

export type ChatSendMessageOptions = {
  label?: string
  isSystemUser?: boolean
}

export type ChatType = 'other' | 'group' | 'task' | 'personal' | 'news'

/**
 * @description
 * Allows to manipulate tasks’ chats.
 * @descriptionRu
 * Позволяет управлять чатами задач.
 */
export class Chat extends ContextObject {
  public chatListenerId: UUID
  private static _chatInputPlaceholder?: string
  private static _chatInputPlaceholderListeners: ((
    placeholder?: string
  ) => void)[] = []
  private static _clearChatInputListeners: (() => void)[] = []
  /**
   * @description
   * Allows to insert special chunks into chat messages.
   * @descriptionRu
   * Позволяет вставлять специальные чанки в сообщения чата.
   */
  public chunks = {
    /**
     * @code
     * Chat.postMessage(openedChatItem, Current.user, `Hello, ${Chat.chunks.mention("0b9abfe3-c438-4350-859c-b0085110084f")}!`);
     * // Result in chat: Hello, @User!
     * @description
     * Returns a mention chunk for the user with specified ID. User will receive a notification about this mention.
     * @descriptionRu
     * Возвращает чанк упоминания пользователя с указанным ID. Пользователь получит уведомление об этом упоминании.
     * @param userId
     */
    mention(userId: DataObjectId): string {
      return `[!mention:${userId}!]`
    }
  }

  public override afterLoad(): void {
    this.chatListenerId = ChatUtils.addChatChangeListener(
      this.onChatChangeEventHandler.bind(this),
      true
    )
  }

  public override beforeUnload(): void {
    ChatUtils.removeChatChangeListener(this.chatListenerId)
  }

  private onChatChangeEventHandler(): void {
    const chatHub = ChatUtils.getOpenedChatHub()
    this.onChatChange?.(chatHub)
  }

  public static override registered: Map<DataObjectId, Map<string, Chat>> =
    new Map()

  /**
   * @description
   * Closes currently opened chat if it is opened
   * @descriptionRu
   * Закрывает текущий открытый чат.
   * @code
   * Chat.close();
   * @example
   * Chat.close();
   */
  public close(): void {
    if (this.context.box.companyId === globals.getCompanyId()) {
      ChatUtils.closeChatIfOpened()
    }
  }

  /**
   * @code
   * Chat.open(Items.get('... task id'));
   * @description
   * Opens a chat for the specified task.
   * @descriptionRu
   * Открывает чат указанной задачи.
   */
  public open(task: Item): void {
    if (!task || task.box.companyId !== this.context.box.companyId) {
      logger.error('Incorrect arguments')
      return
    }
    ChatUtils.openChat(Items.toHub(task))
  }

  /**
   * @code
   * Chat.listMessages(Items.get('... task id'));
   * @description
   * Returns an array of all messages in chat.
   * @descriptionRu
   * Возвращает массив всех сообщений в чате.
   * @example
   * var task = Items.get('... task id');
   * var messages = Chat.listMessages(task);
   * console.log('First message', message[0]);
   */
  public listMessages(task: Item): MessageObj[] {
    if (!task || task.box.companyId !== this.context.box.companyId) {
      logger.error('Incorrect arguments')
      return []
    }
    const messages: ChatMessage[] = [
      ...Items.toHub(task).chat.messages.values()
    ]
    return messages.map(
      (msg: ChatMessage) => new MessageObj(task.box, task.id, msg.timestamp)
    )
  }

  /**
   * @deprecated use Chat.sendMessage() or Chat.sendSystemMessage() instead
   * @code
   * Chat.postMessage(Items.get('...'), Users.get('...'), '... text');
   * @description
   * Posts a message to the specified chat.
   * @descriptionRu
   * Отправляет сообщение в чат по задаче.
   * @example
   * var task = Items.get('...');
   * // sending service message (empty sender)
   * Chat.postMessage(task, '', 'Hello');
   *
   * // sending message from some user
   * Chat.postMessage(task, Users.get('...'), 'Hello!');
   */
  public postMessage(
    task: Item,
    from: UserObj,
    text: string,
    label?: string
  ): void {
    this.sendMessage(task, text, {
      label,
      isSystemUser: !from || (from && from.id === youGileUserId)
    })
  }

  /**
   * @code
   * Chat.sendMessage(Items.get('...'), '... text');
   * @description
   * Posts a message to the specified chat from current user.
   * @descriptionRu
   * Отправляет сообщение в чат по задаче от лица текущего пользователя.
   * @example
   * var task = Items.get('...');
   * // sending system message
   * Chat.sendMessage(task, 'Hello', {isSystemUser: true});
   *
   * // or better use alias-method to send system this case:
   * Chat.sendSystemMessage(task, 'Hello');
   *
   * // sending message from current user
   * Chat.sendMessage(task, 'Hello!');
   */
  public sendMessage(
    task: Item,
    text: string,
    options?: ChatSendMessageOptions
  ): void {
    const mentions = getMatchedMentions(text)

    const mentionsWithNames = ArrUtil.defined(
      mentions.map(({userId, match}) => {
        const user = Users.get(userId)
        if (!user) {
          return undefined
        }

        return {
          userId,
          match,
          name: user.name
        }
      })
    )

    const {text: finalText, chunks} = getChunksWithFixedText(
      text,
      mentionsWithNames
    )

    const hub = Items.toHub(task)

    const chatMessage = ChatUtils.postOrUpdateMessage({
      hub,
      message: finalText,
      params: {
        chunks
      },
      editedMessageTimestamp: undefined,
      messageToReply: undefined,
      isSystemUser: options?.isSystemUser
    })

    if (options?.label && chatMessage) {
      hub.chat.addLabel(chatMessage.timestamp, options?.label)
    }

    hub.commit()
    this.scrollBottom()
  }

  /**
   * @code
   * Chat.sendSystemMessage(Items.get('...'), '... text');
   * @description
   * Posts a message to the specified chat from system user.
   * @descriptionRu
   * Отправляет сообщение в чат по задаче от системного пользоавтеля.
   * @example
   * var task = Items.get('...');
   * // sending service message
   * Chat.sendSystemMessage(task, 'Hello');
   */
  public sendSystemMessage(
    task: Item,
    text: string,
    options?: ChatSendMessageOptions
  ): void {
    this.sendMessage(task, text, {
      ...options,
      isSystemUser: true
    })
  }

  /**
   * @code
   * Chat.getDescription(Items.get('...'));
   * @description
   * Gets chat description string. Generally it is html-string.
   * @descriptionRu
   * Возвращает строку с описанием к задаче (html-строка).
   */
  public getDescription(task: Item): string {
    if (!task || task.box.companyId !== this.context.box.companyId) {
      logger.error('Incorrect arguments')
      return undefined
    }
    const hub = Items.toHub(task)
    return hub.description
  }

  /**
   * @code
   * Chat.setDescription(Items.get('...'), '... html string');
   * @description
   * Sets a new description for a task’s chat. HTML-string can
   * be passed.
   * @descriptionRu
   * Устанавливает описание к задаче. Можно передавать HTML-строку
   * как параметр.
   */
  public setDescription(task: Item, text: string): void {
    if (!task || task.box.companyId !== this.context.box.companyId) {
      logger.error('Incorrect arguments')
      return undefined
    }
    const hub = Items.toHub(task)
    hub.description = HTMLUtil.sanitizeHtml(text)
    hub.commit()
  }

  /**
   * @code
   * Chat.getSubtasks(Items.get('...'));
   * @description
   * Gets all subtask lists with their items as a JSON object.
   * @descriptionRu
   * Возвращает списки чеклистов со всеми их пунктами в виде
   * JSON-объекта.
   * @example
   * var task = Items.get('...');
   * var todoLists = Chat.getSubtasks(task);
   * console.log(todoLists);
   * // result:
   * // [
   * //   {
   * //     id: '...',
   * //     title: 'Todo list 1',
   * //     items: [
   * //       {
   * //         id: '...',
   * //         isCompleted: false,
   * //         title: 'Todo item 1'
   * //       }
   * //     ]
   * //   }
   * // ]
   * //
   */
  public getSubtasks(task: Item): Checklist[] {
    if (!task || task.box.companyId !== this.context.box.companyId) {
      logger.error('Incorrect arguments')
      return undefined
    }
    const hub = Items.toHub(task)
    return Util.clone(getChecklists(hub) || [])
  }

  /**
   * @code
   * Chat.setSubtasks(Items.get('...'), [...]);
   * @description
   * Sets all subtask lists for the specified task.
   * @descriptionRu
   * Позволяет установить списки чеклистов для выбранной задачи.
   * @example
   * var task = Items.get('...');
   * var todoLists = Chat.getSubtasks(task);
   * var firstTodoList = todoLists[0];
   * var newTask = {
   *   title: 'Do something…'
   * };
   * firstTodoList.items.push(newTask);
   * // updating todo lists
   * Chat.setSubtasks(task, todoLists);
   */
  public setSubtasks(task: Item, subtasks: any[]): void {
    if (
      !task ||
      task.box.companyId !== this.context.box.companyId ||
      !subtasks
    ) {
      logger.error('Incorrect arguments')
      return undefined
    }
    const hub = Items.toHub(task)
    Task.setChecklists(hub, Util.clone(subtasks))
    hub.commit()
  }

  /**
   * @code
   * Chat.addUser(Items.get('... task id'), Users.get('...'));
   * @description
   * Adds user to a chat.
   * @descriptionRu
   * Добавляет пользователя в чат.
   */
  public addUser(task: Item, user: UserObj): void {
    new Users(this.context, {}).addToChat(user, task)
  }

  /**
   * @code
   * Chat.removeUser(Items.get('... task id'), Users.get('...'));
   * @description
   * Removes user from chat.
   * @descriptionRu
   * Убирает пользователя из чата.
   */
  public removeUser(task: Item, user: UserObj): void {
    new Users(this.context, {}).removeFromChat(user, task)
  }

  /**
   * @code
   * Chat.listUsers(Items.get('... task id'));
   * @description
   * Returns an array of all notified users in the specified chat.
   * @descriptionRu
   * Возвращает массив всех пользователей в чате (получающих
   * уведомления по указанному чату).
   */
  public listUsers(task: Item): UserObj[] {
    return new Users(this.context, {}).listAllInChat(task)
  }

  /**
   * @code
   * Chat.isUserInChat(Items.get('... task id'), Users.get('...'));
   * @description
   * Returns true if the user is in the chat.
   * @descriptionRu
   * Возвращает true если пользователь добавлен в чат.
   */
  public isUserInChat(task: Item, user: UserObj): boolean {
    return new Users(this.context, {}).isInChat(user, task)
  }

  /**
   * @code
   * Chat.scrollBottom();
   * @description
   * Scrolls an opened chat to bottom.
   * @descriptionRu
   * Прокручивает открытый чат в конец.
   */
  public scrollBottom(): void {
    if (this.context.box.companyId !== globals.getCompanyId()) {
      logger.warn('Skipped scrollBottom()')
      return
    }
    const element = document.querySelector('.chat-item-messages')
    ChatUtils.scrollBottom(element as HTMLElement)
  }

  /**
   * @code
   * Chat.scrollTop();
   * @description
   * Scrolls opened chat to top.
   * @descriptionRu
   * Прокручивает открытый чат в начало.
   */
  public scrollTop(): void {
    if (this.context.box.companyId !== globals.getCompanyId()) {
      return
    }
    const element = document.querySelector('.chat-item-messages')
    ChatUtils.scrollTop(element as HTMLElement)
  }

  /**
   * @code
   * Chat.markAsSeen(Items.get('... task id'));
   * @description
   * Marks all messages in the chat as seen.
   * @descriptionRu
   * Отмечает все сообщения в чате по задаче как прочитанные.
   */
  public markAsSeen(task: Item, user?: UserObj): void {
    if (!user || !task || task.box.companyId !== this.context.box.companyId) {
      return
    }
    const hub = Items.toHub(task)
    ChatUtils.markAsSeen(hub, user)
  }

  /**
   * @deprecated Use Chat.markAsSeen(task, user) instead.
   */
  public markRead(task: Item, user: UserObj): void {
    this.markAsSeen(task, user)
  }

  /**
   * @code
   * Chat.hasUnseenChats() or with filtering:
   * Chat.hasUnseenChats((chat) => chat.getNewMentionsAmount() > 0)
   * @description
   * Returns whether the current user unseen chats.
   * @descriptionRu
   * Возвращает имеет ли текущий пользователь непросмотренные чаты.
   */
  public hasUnseenChats(filter?: (chat: ChatProxy) => boolean): boolean {
    const box = Sync.getCurrentSandbox()

    return ChatUtils.getChats(
      box,
      NotificationSettingsService.getNotificationSettings()
    ).some(
      filter ??
        (chat => chat.newMessagesAmount > 0 || chat.getNewMentionsAmount() >= 0)
    )
  }

  /**
   * @code
   * Chat.markAllAsSeen() or with filtering:
   * Chat.markAllAsSeen((chat) => chat.getNewMentionsAmount() > 0)
   * @description
   * Marks chats as seen.
   * @descriptionRu
   * Отмечает чаты как прочитанные.
   */
  public markAllAsSeen(filter?: (chat: ChatProxy) => boolean): void {
    const box = Sync.getCurrentSandbox()

    const chats = ChatUtils.getChats(
      box,
      NotificationSettingsService.getNotificationSettings()
    )

    for (const chat of chats) {
      const shouldMark = filter
        ? filter(chat)
        : chat.newMessagesAmount > 0 || chat.getNewMentionsAmount() > 0

      if (shouldMark) {
        chat.markAsSeen()
      }
    }
  }

  /**
   * @code
   * Chat.experimentalGetChatTypeByChatHub(chatHub);
   * @description
   * Returns chat type by chat hub.
   * @descriptionRu
   * Возвращает тип чата по хабу чата.
   * @experimental
   * @param chatHub
   */
  public experimentalGetChatTypeByChatHub(chatHub: Hub): ChatType {
    if (!chatHub) {
      return 'other'
    }
    const box = Sync.get(chatHub)
    const type = {
      [Sync.profileId as string]: 'direct',
      [newsCompanyId]: 'news'
    }[box.companyId]
    // old hash version
    if (GroupChat.isGroupChat(chatHub)) {
      return 'group'
    }
    if (
      Task.isRepeatTemplate(chatHub) ||
      Task.isMovementTemplate(chatHub) ||
      Task.isTaskTemplate(chatHub)
    ) {
      return 'other'
    }

    if (type) {
      return type as ChatType
    }

    const idTask = idTasksApi.getIdTask(chatHub.id)

    if (idTask) {
      /**
       * Strange, but sometimes idTask is not defined even for task
       * @todo investigate
       */
      return 'task'
    }

    return 'task'
  }

  /**
   * @code
   * Chat.experimentalGetChatTypeByChatHub(chatItem);
   * @description
   * Returns chat type.
   * @descriptionRu
   * Возвращает тип чата.
   * @experimental
   * @param chatItem
   */
  public experimentalGetChatTypeByChatItem(chatItem: Item): ChatType {
    if (!chatItem) {
      return 'other'
    }
    const hub = Items.toHub(chatItem)
    return this.experimentalGetChatTypeByChatHub(hub)
  }

  /**
   * @code
   * Chat.onPostMessage = function (message) {
   *
   * };
   * @description
   * This function is called when user added a message to a chat.
   * @descriptionRu
   * Эта функция вызывается, когда пользователь добавил
   * сообщение в чат.
   * @example
   * Chat.postMessage = function (message) {
   *   message.text += '!!!';
   * }
   */
  public onPostMessage: (message: MessageObj) => boolean

  /**
   * @code
   * Chat.onBeforePostMessage = function (task, text) {
   *   return true;
   * };
   * @description
   * This function is called when user posts a message and before
   * message is added to the chat. If it returns false, then
   * message is not added.
   * @descriptionRu
   * Эта функция вызывается, когда пользователь отправляет сообщение,
   * но до того, как оно добавляется в чат. Если функция вернёт
   * false, то сообщение не добавится в чат.
   */
  public onBeforePostMessage: (task: Item, text: string) => boolean

  /**
   * @code
   * Chat.onBeforePostMessageTransform = function (task, text, isEditing) {
   *  return text.replace(/(https?:\/\/[^\s]+)/g, '<a href="$1" target="_blank">$1</a>');
   *  };
   * @description
   * This function is called when user posts a message and before message is added to the chat. It allows to transform message text before posting.
   * @descriptionRu
   * Эта функция вызывается, когда пользователь отправляет сообщение, но до того, как оно добавляется в чат. Позволяет трансформировать текст сообщения перед отправкой.
   */
  public onBeforePostMessageTransform: (
    task: Item,
    text: string,
    isEditing: boolean
  ) => string

  /**
   * @deprecated Use getOpenedChatItem instead
   * @code
   * Chat.getOpenedChat();
   * @description
   * Returns an opened chat.
   * @descriptionRu
   * Возвращает открытый чат.
   */
  public getOpenedChatId(): DataObjectId | undefined {
    const chatHub = ChatUtils.getOpenedChatHub()
    if (!chatHub) {
      return undefined
    }
    return chatHub?.id
  }

  /**
   * @code
   * Chat.getOpenedChat();
   * @description
   * Returns an opened chat item.
   * @descriptionRu
   * Возвращает Item открытого чата.
   */
  public getOpenedChatItem(): Item | undefined {
    const chatHub = ChatUtils.getOpenedChatHub()
    if (!chatHub) {
      return undefined
    }
    return new Item(chatHub.id, this.context.box)
  }

  public getIdTask(hubId: DataObjectId): string | undefined {
    const idTask = idTasksApi.getIdTask(hubId)
    if (!idTask) {
      return undefined
    }
    return idTask
  }

  /**
   * @code
   * Chat.onChatChange = function (chatHub) {}
   * @description
   * This function is called when the opened chat is changed.
   * @descriptionRu
   * Эта функция вызывается, когда открытый чат изменяется.
   */
  public onChatChange: (chatHub: Hub | undefined) => void

  /**
   * @code
   * UIHandlers.onChatInputValueChange = function (value) {};
   * @example
   * UIHandlers.onChatInputValueChange = function (value) {
   *   console.log(value);
   * };
   *
   * @description
   * This function is triggered when the value of the chat input changes.
   * @descriptionRu
   * Эта функция вызывается, когда меняется значение ввода чата.
   */
  public onChatInputValueChange?: (value: string) => void

  public static chatInputValueChange(value: string): void {
    this.forEachCurrent((h: Chat) => {
      if (!h?.onChatInputValueChange) {
        return true
      }
      try {
        h.onChatInputValueChange(value)
      } catch (e) {
        logger.error('Error in configurator onChatInputValueChange', e)
      }
      return true
    })
  }

  /**
   * @code
   * UIHandlers.onChatInputKeydown = function (data) {
   *   if (data.keyCode === 8) {
   *     console.log('Backspace pressed')
   *   }
   * };
   * @description
   * This function is triggered when a key is pressed in the chat input.
   * @descriptionRu
   * Эта функция вызывается, когда нажата клавиша в поле ввода чата.
   */
  public onChatInputKeydown?: (data: ChatInputKeydownData) => void

  public static chatInputKeydown(data: ChatInputKeydownData): void {
    this.forEachCurrent((h: Chat) => {
      if (!h?.onChatInputKeydown) {
        return true
      }
      try {
        h.onChatInputKeydown(data)
      } catch (e) {
        logger.error('Error in configurator onChatInputKeydown', e)
      }
      return true
    })
  }

  /**
   * @code
   * UIHandlers.onShowPopupVoting = function () {};
   * @description
   * This function is triggered when the create voting popup is shown.
   * @descriptionRu
   * Эта функция вызывается, когда показывается всплывающее окно создания голосования.
   */
  public onShowPopupVoting?: () => void

  public static showPopupVoting(): void {
    this.forEachCurrent((h: Chat) => {
      if (!h?.onShowPopupVoting) {
        return true
      }
      try {
        h.onShowPopupVoting()
      } catch (e) {
        logger.error('Error in configurator onShowPopupVoting', e)
      }
      return true
    })
  }

  /**
   * @code
   * UIHandlers.onStartScreencast = function () {};
   * @description
   * This function is triggered when the screencast tool is started.
   * @descriptionRu
   * Эта функция вызывается, когда запускается инструмент для записи экрана.
   */
  public onStartScreencast?: () => void

  public static startScreencast(): void {
    this.forEachCurrent((h: Chat) => {
      if (!h?.onStartScreencast) {
        return true
      }
      try {
        h.onStartScreencast()
      } catch (e) {
        logger.error('Error in configurator onStartScreencast', e)
      }
      return true
    })
  }

  public onActiveChatMessagesChange?: (
    chatId: DataObjectId | undefined,
    messages: ChatMessage[]
  ) => void

  public static activeChatMessagesChange(
    chatId: DataObjectId | undefined,
    messages: ChatMessage[]
  ): void {
    this.forEachCurrent((h: Chat) => {
      if (!h?.onActiveChatMessagesChange) {
        return true
      }
      try {
        h.onActiveChatMessagesChange(chatId, messages)
      } catch (e) {
        logger.error('Error in configurator onActiveChatMessagesChange', e)
      }
      return true
    })
  }

  /**
   * @code
   * Chat.setChatInputPlaceholder('... placeholder');
   * @description
   * Sets a placeholder for the chat input. Pass undefined to reset to default.
   * @descriptionRu
   * Устанавливает placeholder для ввода чата. Передайте undefined, чтобы сбросить к значению по умолчанию.
   * @param placeholder
   */
  public setChatInputPlaceholder(placeholder?: string): void {
    Chat._chatInputPlaceholder = placeholder
    Chat._chatInputPlaceholderListeners.forEach(cb =>
      cb(Chat._chatInputPlaceholder)
    )
  }

  /**
   * @code
   * Chat.clearChatInput();
   * @description
   * Clears the chat input.
   * @descriptionRu
   * Очищает ввод чата.
   */
  public clearChatInput() {
    Chat._clearChatInputListeners.forEach(cb => cb())
  }

  public static internalWatchChatInputPlaceholder(
    cb: (placeholder?: string) => void
  ): () => void {
    this._chatInputPlaceholderListeners.push(cb)
    cb(this._chatInputPlaceholder)
    return () => {
      this._chatInputPlaceholderListeners =
        this._chatInputPlaceholderListeners.filter(c => c !== cb)
    }
  }

  public static internalListenClearChatInput(cb: () => void): () => void {
    this._clearChatInputListeners.push(cb)
    return () => {
      this._clearChatInputListeners = this._clearChatInputListeners.filter(
        c => c !== cb
      )
    }
  }

  /**
   * @todo implement
   */
  public onOpen: () => void

  /**
   * @todo implement
   */
  public onClose: () => void

  /**
   * @todo implement
   */
  public allowDescription: () => void

  /**
   * @todo implement
   */
  public allowChat: () => void

  /**
   * @todo implement
   */
  public allowEditDescription: () => void

  /**
   * @todo implement
   */
  public allowPostMessages: () => void

  /**
   * @todo implement
   */
  public onDescriptionChange: () => void

  /**
   * @todo implement
   */
  public onReadMessage: () => void

  /**
   * @todo implement
   */
  public onKeyDown: () => void

  /**
   * @todo implement
   */
  public onKeyUp: () => void

  public static postMessage(message: MessageObj): void {
    if (!message?.box) {
      return
    }
    if (message.box.companyId !== globals.getCompanyId()) {
      return
    }
    Chat.forEachCurrent((i: Chat) => {
      if (i.onPostMessage) {
        i.onPostMessage(message)
      }
      return true
    })
  }

  public static beforePostMessage(task: Hub, text: string): boolean {
    if (!task) {
      return true
    }
    if (task.companyId !== globals.getCompanyId()) {
      return true
    }
    return Chat.forEachCurrent((i: Chat) =>
      i.onBeforePostMessage
        ? i.onBeforePostMessage(Items.fromHub(task), text)
        : true
    )
  }

  public static beforePostMessageTransform(
    task: Hub,
    text: string,
    isEditing: boolean
  ): string {
    let finalText = text
    const box = Sync.get(task)
    if (box.companyId !== globals.getCompanyId()) {
      return text
    }
    const chatItem = Items.fromHub(task)
    if (!chatItem) {
      return text
    }
    Chat.forEachCurrent((i: Chat) => {
      finalText = i.onBeforePostMessageTransform
        ? i.onBeforePostMessageTransform(chatItem, finalText, isEditing)
        : finalText
      return true
    })
    return finalText
  }

  public setFilterChatList(func?: FilterChatListType) {
    apiChatList.filterChatList = func
  }
  public triggerChatList() {
    apiChatList.triggerChatList?.()
  }
  public setAreDefaultFiltersDisabled(disabled: boolean) {
    apiChatList.setAreDefaultFiltersDisabled?.(disabled)
  }
}
